import React, { memo } from 'react';
import { motion } from 'framer-motion';
import LazyImage from './LazyImage';
import DOMPurify from 'dompurify';
import { useDarkMode } from '../contexts/DarkModeContext';

// Security: Enhanced text sanitization
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  
  // Remove null bytes and control characters
  let sanitized = text.replace(/[\x00-\x1F\x7F]/g, '');
  
  // Remove potential script injection patterns
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');
  sanitized = sanitized.replace(/vbscript:/gi, '');
  
  // Apply DOMPurify with strict settings
  return DOMPurify.sanitize(sanitized, { 
    ALLOWED_TAGS: [], 
    ALLOWED_ATTR: [],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
  });
};



const MessageBubble = memo(({ 
  msg, 
  user, 
  isAdminDashboard, 
  conversation, 
  onImageClick,
  renderMessageStatus,
  onReplyClick,
  replyToMessage,
  onContextMenu
}) => {
  const { isDarkMode } = useDarkMode();

  // 🎨 REPLY CONTEXT: WhatsApp-style reply preview component
  const ReplyContext = ({ replyToMessage, isMe, isAdminMsg }) => {
    if (!replyToMessage) return null;

    // Get display name for the replied message
    const getReplySenderName = () => {
      if (replyToMessage.name) return replyToMessage.name;
      if (replyToMessage.from) {
        // Extract name from email or use email as fallback
        const email = replyToMessage.from;
        const name = email.split('@')[0];
        return name.charAt(0).toUpperCase() + name.slice(1);
      }
      return 'Unknown User';
    };

    // Get reply content preview
    const getReplyContent = () => {
      if (replyToMessage.text) {
        return replyToMessage.text.length > 50 
          ? replyToMessage.text.substring(0, 50) + '...' 
          : replyToMessage.text;
      }
      if (replyToMessage.type === 'image') return '📷 Image';
      if (replyToMessage.type === 'video') return '🎥 Video';
      if (replyToMessage.type === 'file') return '📎 File';
      return 'Message';
    };

    return (
      <div className="mb-2 p-2 rounded-lg bg-black bg-opacity-20 border-l-4 border-green-400">
        <div className="text-xs text-green-300 font-medium mb-1">
          {getReplySenderName()}
        </div>
        <div className={`text-xs truncate ${
          isDarkMode ? 'text-gray-300' : 'text-gray-600'
        }`}>
          {getReplyContent()}
        </div>
      </div>
    );
  };
  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize expensive calculations
  // Fix: Properly identify if the current user sent this message
  // Simplified and more robust logic
  const isMe = user && (
    // Check if message is from current user by email
    (msg.from && user.email && msg.from.toLowerCase() === user.email.toLowerCase()) ||
    // Check if message is from current user by UID
    (msg.from && user.uid && msg.from === user.uid) ||
    // Check if message is from current user by fromUid field
    (msg.fromUid && user.uid && msg.fromUid === user.uid) ||
    (msg.fromUid && user.email && msg.fromUid === user.email) ||
    // Check legacy fields
    (msg.senderId && user.uid && msg.senderId === user.uid) ||
    // Admin check
    (msg.isAdmin && user.email === 'ytproudltd@gmail.com')
  );

  const isAdminMsg = msg.from === 'ytproudltd@gmail.com' || msg.name === 'YTproud Agent';
  const isMeAdminDashboard = isAdminDashboard && user && user.email === 'ytproudltd@gmail.com' && (
    msg.from === user.uid || 
    msg.from === user.email || 
    msg.fromUid === user.uid || 
    msg.fromUid === user.email
  );
  const isSeller = conversation && conversation.seller && (
    msg.senderId === conversation.seller.uid ||
    msg.from === conversation.seller.uid ||
    msg.from === conversation.seller.email ||
    msg.fromUid === conversation.seller.uid ||
    msg.fromUid === conversation.seller.email
  );
  
  // ⚡️ PERFORMANCE OPTIMIZATION: Memoize avatar URL calculation
  const sellerAvatar = conversation && (conversation.seller?.avatar || conversation.seller_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(conversation.seller?.name || conversation.seller_name || 'Seller')}`);

  // 🎨 CONTEXT MENU: Handle right-click to show context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    if (onContextMenu) {
      onContextMenu(e, msg);
    }
  };

  // Debug logging
  console.log('MessageBubble Debug:', {
    msgId: msg.id,
    msgFrom: msg.from,
    msgName: msg.name,
    userEmail: user?.email,
    userUid: user?.uid,
    isMe,
    isAdminMsg,
    isMeAdminDashboard,
    isSeller,
    isDarkMode
  });

  // Render bubbles
  if (isMeAdminDashboard) {
    // Admin dashboard, admin's own message (right, agent style)
    return (
      <motion.div 
        className="flex justify-end items-end gap-2 mb-2"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 0.3, 
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
      >
        <div className="flex flex-col items-end max-w-[70%]">
          <div className="flex items-center gap-2 mb-1 justify-end">
            <span className="font-bold">YTproud Agent</span>
            <span className="ml-1 px-2 py-0.5 text-xs rounded text-white align-middle font-semibold" style={{ background: '#7f7f7f' }}>Agent</span>
          </div>
          {msg.type === 'image' && msg.url ? (
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200 bg-white p-1 mt-1 cursor-pointer" onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl w-full h-auto object-contain rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className="max-w-xs max-h-60 rounded-2xl mt-1" />
          ) : (
            <div className={`${
              isDarkMode 
                ? 'bg-[#005C4B] text-white shadow-xl' // WhatsApp dark mode sent message color for admin
                : 'bg-black text-white shadow-lg'
            } rounded-2xl rounded-br-sm px-5 py-3 border-2 font-semibold break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-xl relative cursor-pointer ${
              msg.isTampered ? 'border-red-500 bg-red-900' : 
              msg.isPending ? 'border-yellow-400 bg-gray-800' : 'border-purple-500'
            }`} onContextMenu={handleContextMenu}>
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {sanitizeText(msg.text)}
                  {msg.isTampered && (
                    <div className="text-xs text-red-300 mt-1">
                      ⚠️ Message integrity compromised
                    </div>
                  )}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        isDarkMode ? 'bg-gray-400' : 'bg-gray-500'
                      }`}></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-400">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <img
          src={isSeller ? sellerAvatar : (user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email || 'User')}`)}
          alt="YTproud Agent"
          className="w-8 h-8 rounded-full object-cover border-2 border-white shadow"
        />
      </motion.div>
    );
  } else if (isMe) {
    // User's own message (right, red bubble)
    return (
      <motion.div 
        className="flex justify-end items-end gap-2 mb-2"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 0.3, 
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
      >
        <div className="flex flex-col items-end max-w-[70%]">
          {msg.type === 'image' && msg.url ? (
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200 bg-white p-1 mt-1 cursor-pointer" onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl w-full h-auto object-contain rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className="max-w-xs max-h-60 rounded-2xl mt-1" />
          ) : (
            <div 
              className={`${
                isDarkMode 
                  ? 'bg-[#005C4B] text-white shadow-lg' // WhatsApp dark mode sent message color
                  : 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md'
              } rounded-2xl rounded-br-sm px-5 py-3 text-base break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl relative cursor-pointer`}
              onContextMenu={handleContextMenu}
            >
              {/* 🎨 REPLY CONTEXT: Show reply information */}
              {msg.replyTo && (
                <ReplyContext 
                  replyToMessage={msg.replyTo} 
                  isMe={isMe} 
                  isAdminMsg={isAdminMsg} 
                />
              )}
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {sanitizeText(msg.text)}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${
                        isDarkMode ? 'bg-gray-300' : 'bg-gray-400'
                      }`}></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-300">
                      <div className="w-2 h-2 bg-red-300 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <img
          src={isSeller ? sellerAvatar : (user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email || 'User')}`)}
          alt="Me"
          className="w-8 h-8 rounded-full object-cover border-2 border-white shadow"
        />
      </motion.div>
    );
  } else if (isAdminMsg) {
    // Agent/admin message (left, agent style)
    return (
      <motion.div 
        className="flex items-end gap-2 mb-2"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 0.3, 
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
      >
        <img
          src={isSeller ? sellerAvatar : (user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.email || 'User')}`)}
          alt={msg.name}
          className="w-8 h-8 rounded-full object-cover border-2 border-white shadow"
        />
        <div className="flex flex-col items-start max-w-[70%]">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-bold">YTproud Agent</span>
            <span className="ml-1 px-2 py-0.5 text-xs rounded text-white align-middle font-semibold" style={{ background: '#7f7f7f' }}>Agent</span>
          </div>
          {msg.type === 'image' && msg.url ? (
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200 bg-white p-1 mt-1 cursor-pointer" onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl w-full h-auto object-contain rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className="max-w-xs max-h-60 rounded-2xl mt-1" />
          ) : (
            <div 
              className={`${
                isDarkMode 
                  ? 'bg-[#202C33] text-white shadow-xl' // WhatsApp dark mode received message color for admin
                  : 'bg-black text-white shadow-lg'
              } rounded-2xl rounded-bl-sm px-5 py-3 border-2 border-purple-500 font-semibold break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl relative cursor-pointer`}
              onContextMenu={handleContextMenu}
            >
              {/* 🎨 REPLY CONTEXT: Show reply information */}
              {msg.replyTo && (
                <ReplyContext 
                  replyToMessage={msg.replyTo} 
                  isMe={isMe} 
                  isAdminMsg={isAdminMsg} 
                />
              )}
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {sanitizeText(msg.text)}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-300'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-400">
                      <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  } else {
    // Other user message (left, blue bubble)
    return (
      <motion.div 
        className="flex items-end gap-2 mb-2"
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          duration: 0.3, 
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
      >
        <img
          src={isSeller ? sellerAvatar : (msg.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(msg.name || 'User')}`)}
          alt={msg.name}
          className="w-8 h-8 rounded-full object-cover border-2 border-white shadow"
        />
        <div className="flex flex-col items-start max-w-[70%]">
          {msg.type === 'image' && msg.url ? (
            <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200 bg-white p-1 mt-1 cursor-pointer" onClick={() => onImageClick(msg.url)}>
              <LazyImage 
                src={msg.url} 
                alt="shared" 
                className="max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl w-full h-auto object-contain rounded-2xl" 
              />
            </div>
          ) : msg.type === 'video' && msg.url ? (
            <video src={msg.url} controls className="max-w-xs max-h-60 rounded-2xl mt-1" />
          ) : (
            <div 
              className={`${
                isDarkMode 
                  ? 'bg-[#202C33] text-white shadow-lg' // WhatsApp dark mode received message color
                  : 'bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow'
              } rounded-2xl rounded-br-sm px-5 py-3 text-base break-words whitespace-pre-line max-w-[90vw] sm:max-w-[70vw] md:max-w-lg lg:max-w-xl relative cursor-pointer`}
              onContextMenu={handleContextMenu}
            >
              {/* 🎨 REPLY CONTEXT: Show reply information */}
              {msg.replyTo && (
                <ReplyContext 
                  replyToMessage={msg.replyTo} 
                  isMe={isMe} 
                  isAdminMsg={isAdminMsg} 
                />
              )}
              <div className="flex items-end justify-between gap-2">
                <div className="flex-1">
                  {sanitizeText(msg.text)}
                </div>
                
                {/* ⚡️ WHATSAPP STYLE: Time and status on same line as text */}
                <div className={`flex items-center gap-1 text-xs flex-shrink-0 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-200'
                }`}>
                  <span>{msg.time}</span>
                  {renderMessageStatus(msg)}
                  {msg.status === 'sending' && (
                    <span className="flex items-center gap-1 ml-1">
                      <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                      sending...
                    </span>
                  )}
                  {msg.status === 'failed' && (
                    <span className="flex items-center gap-1 ml-1 text-red-300">
                      <div className="w-2 h-2 bg-red-300 rounded-full"></div>
                      failed
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  }
});

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
